import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X, Search, Loader2, Mail, Phone, MapPin, Home, CalendarClock, CalendarCheck,
    SlidersHorizontal, Building2, Gauge,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../api';
import { syncVirtualContact } from '../../../api/contact';
import BuyerMatchPropertiesModal from './BuyerMatchPropertiesModal';
import BuyerPreferencesModal from '../../contacts/components/BuyerPreferencesModal';
import ContactNotesButton from '../../contacts/components/ContactNotesButton';
import { preferenceParts } from '../utils/buyerPreferenceSummary';

/**
 * Who is behind one number on the buyer search status board.
 *
 * Monday 13068998532, Christian Gravias 24 Sep 2026: "Make the buttons
 * clickable". The board answered "how many" and stopped there, so a count of
 * purchased buyers or property management leads could not be actioned by
 * anyone. Every tile, every "why they stopped looking" row and the property
 * management line now opens this list, and every person in it opens their CRM
 * record.
 *
 * The list comes from the same de-duplicated person index as the counts
 * (buyer_lifecycle._person_index), so it can never disagree with the number
 * that was clicked.
 *
 * Michael Piccoli, 29 Sep 2026: "It would be good to view where they have
 * purchased in this list and the date of purchase". A purchased row now carries
 * both, from wherever the answer came from: the buyer on the portal or the
 * emailed page, or an agent typing it on the contact record after the call.
 * Neither is compulsory, so a row shows whichever it has and says so plainly
 * when it has neither.
 *
 * `withMatches` (Find Buyers, DOMA 13374913603): each row also shows what the
 * buyer wants to buy and a "Match properties" button. Christian's follow-up
 * (26 Oct 2026): "line up the match properties all to be on the right" and
 * "add \"Buying preferences\" & \"notes\" button", so the three sit in one
 * right-hand column on every row whatever the length of the preferences line.
 */

const identityOf = (person) => ({
    contactId: person.contact_id || null,
    buyerId: person.buyer_id || null,
    email: person.email,
    name: person.name,
    phone: person.phone,
});

// Buyer Intent Score bands, as Top Engaged Buyers and Who-to-Call colour them.
const INTENT_TONE = {
    priority: 'bg-red-50 text-red-700', hot: 'bg-orange-50 text-orange-700',
    active: 'bg-amber-50 text-amber-700', warm: 'bg-blue-50 text-blue-700',
    cold: 'bg-gray-100 text-gray-600',
};

/**
 * The person's Buyer Intent Score, which the Find Buyers board ranks by
 * (Christian Gravias, DOMA 13374913603, 26 Oct 2026: rank the buyers with no
 * status by how likely they are to buy). The reasons are the top signals that
 * built the number, so an agent can see why someone sits at the top. Rows from
 * lists that aren't ranked carry no score field and show nothing.
 */
const IntentChip = ({ person }) => {
    if (!('intent_score' in person)) return null;
    if (person.intent_score == null) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 text-gray-400 text-xs px-2 py-0.5"
                title="No portal activity or profile to score yet.">
                <Gauge className="w-3.5 h-3.5" />
                No score yet
            </span>
        );
    }
    const trend = person.intent_trend || 0;
    return (
        <>
            <span
                className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2 py-0.5 ${INTENT_TONE[person.intent_category] || INTENT_TONE.cold}`}
                title="Buyer Intent Score: profile plus portal activity, out of 100."
            >
                <Gauge className="w-3.5 h-3.5" />
                {person.intent_label || 'Score'} {person.intent_score}
                {trend !== 0 && <span className="font-normal">{trend > 0 ? `+${trend}` : trend}</span>}
            </span>
            {(person.intent_reasons || []).length > 0 && (
                <span className="text-xs text-gray-500">{person.intent_reasons.join(' · ')}</span>
            )}
        </>
    );
};

const ACTION_BTN = 'inline-flex items-center justify-center gap-1.5 w-full rounded-lg border text-xs font-semibold px-3 py-1.5';

const DATE = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
});

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : DATE.format(date);
};

/**
 * A purchase date is a plain calendar day, not a moment. Feeding "2026-09-14"
 * to `new Date` parses it as midnight UTC, which prints as the 13th once the
 * browser is behind UTC, so it is formatted from its own parts instead.
 */
const formatDay = (value) => {
    if (!value) return '';
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!parts) return formatDate(value);
    const [, year, month, day] = parts;
    return DATE.format(new Date(Number(year), Number(month) - 1, Number(day)));
};

const PURCHASED = ['purchased_collings', 'purchased_elsewhere'];

/**
 * Who the row's date belongs to. The backend says whether the buyer answered
 * themselves, an agent set it, or it changed automatically (a pause running
 * out), so an agent's or administrator's edit never reads as the buyer
 * responding (DOMA 13374913603). Older answers that never recorded who gave
 * them keep the plain wording.
 */
const dateLabel = (person) => {
    // A preferences update newer than the status answer: say whose it was
    // (DOMA 13460623858: an agent's update counts as much as the buyer's).
    if (person.updated_what === 'preferences' && person.answered_by === 'agent') {
        return person.updated_by_name ? `${person.updated_by_name} updated preferences` : 'Agent updated preferences';
    }
    if (person.updated_what === 'preferences' && person.answered_by === 'buyer') return 'Buyer updated preferences';
    if (person.answered_by === 'agent') return person.status ? 'Set by agent' : 'Updated by agent';
    if (person.answered_by === 'system') return 'Updated automatically';
    if (person.answered_by === 'buyer') return person.status ? 'Buyer answered' : 'Buyer updated preferences';
    // Nobody on Not set has answered; their date is the preferences'.
    return person.status ? 'Answered' : 'Preferences updated';
};

const BuyerSearchStatusPeopleModal = ({
    isOpen, onClose, title, subtitle, query, withMatches = false,
    returnTo = '/analytics?view=profiles', returnLabel = 'Buyer search status',
}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [people, setPeople] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [term, setTerm] = useState('');
    // Row key of the person being opened, not their buyer id: a contact-only
    // row has no buyer id, and `null === null` would disable every one of them.
    const [opening, setOpening] = useState(null);
    const [matching, setMatching] = useState(null);
    const [editing, setEditing] = useState(null);
    // Bumped after preferences are saved so the row shows the new ones. That
    // refetch is quiet: no spinner, so the list keeps its scroll and search.
    const [reloadKey, setReloadKey] = useState(0);
    // Find Buyers' board opens most likely to buy first; "Newest answers" puts
    // the latest answers on top (Christian Gravias, 26 Oct 2026: "make the list
    // where you can click the button to have from newest buyer answering").
    const [order, setOrder] = useState('intent');
    const quietReload = useRef(false);
    // Newest answers, the buyer's or an agent's: "Agents updating it is just as
    // important" (DOMA 13460623858). Each row says whose it was.
    const newestLabel = 'Newest answers';

    // The query object is rebuilt on every render of the panel, so the effect
    // keys off its contents rather than its identity.
    const queryKey = JSON.stringify(query || {});

    useEffect(() => {
        if (!isOpen) return undefined;
        let cancelled = false;
        if (quietReload.current) {
            quietReload.current = false;
        } else {
            setLoading(true);
            setFailed(false);
        }
        (async () => {
            try {
                const res = await api.get('api/buyer-portal/buyer-search-status/people/', {
                    params: {
                        ...(query || {}),
                        ...(withMatches ? { include_preferences: 1 } : {}),
                        ...(withMatches && order === 'newest' ? { order: 'newest' } : {}),
                    },
                });
                if (cancelled) return;
                setPeople(res.data?.people || []);
                setTruncated(Boolean(res.data?.truncated));
            } catch (error) {
                console.error('Failed to load buyer search status people:', error);
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, queryKey, withMatches, reloadKey, order]);

    useEffect(() => {
        if (isOpen) setTerm('');
    }, [isOpen, queryKey]);

    const filtered = useMemo(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return people;
        return people.filter((person) => (
            (person.name || '').toLowerCase().includes(needle)
            || (person.email || '').toLowerCase().includes(needle)
            || (person.phone || '').toLowerCase().includes(needle)
            || (person.purchase_suburb || '').toLowerCase().includes(needle)
            || formatDay(person.purchase_date).toLowerCase().includes(needle)
        ));
    }, [people, term]);

    /**
     * Open the person's CRM record. A portal-only buyer has no Contact row
     * yet, so it is created/linked first — the same path the calculator
     * drill-downs use, which is why those rows are clickable too.
     */
    const rowKey = (person) => (
        `${person.contact_id || 'x'}-${person.buyer_id || 'x'}-${person.email}`
    );

    const openPerson = async (person) => {
        if (opening) return;
        if (person.contact_id) {
            navigate(`/contacts/${person.contact_id}`, {
                state: { returnTo, returnLabel },
            });
            onClose();
            return;
        }
        if (!person.buyer_id) {
            toast.error('This buyer has no CRM record to open.');
            return;
        }
        setOpening(rowKey(person));
        const toastId = toast.loading(`Opening ${person.name}…`);
        try {
            const response = await syncVirtualContact(`buyer_${person.buyer_id}`);
            const contactId = response?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}`, {
                state: { returnTo, returnLabel },
            });
            onClose();
        } catch (error) {
            toast.error('Could not open this buyer.', { id: toastId });
        } finally {
            setOpening(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {withMatches && (
                    <div className="px-5 pt-4 flex items-center gap-1.5" role="group" aria-label="Order the buyers">
                        {[['intent', 'Most likely to buy'], ['newest', newestLabel]].map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                aria-pressed={order === value}
                                onClick={() => setOrder(value)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                    order === value
                                        ? 'bg-gray-900 border-gray-900 text-white'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {!loading && !failed && people.length > 0 && (
                    <div className="px-5 pt-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                                placeholder="Search by name, email, phone, suburb or purchase date"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40 focus:border-[#55C5D0]"
                            />
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-5 space-y-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-400">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : failed ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            We could not load these buyers just now. Close this and try again.
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                            {people.length === 0
                                ? 'Nobody has this answer yet. The list fills in as buyers answer on the portal and in our emails.'
                                : 'No buyer here matches that search.'}
                        </div>
                    ) : (
                        filtered.map((person) => (
                            // A div, not a button: with `withMatches` the row
                            // holds a button of its own, and buttons can't nest.
                            <div
                                role="button"
                                tabIndex={0}
                                key={rowKey(person)}
                                onClick={() => openPerson(person)}
                                onKeyDown={(e) => {
                                    // Keys pressed on the Match button belong to it.
                                    if (e.target !== e.currentTarget) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openPerson(person);
                                    }
                                }}
                                aria-disabled={opening === rowKey(person)}
                                className={`w-full text-left rounded-xl border border-gray-100 bg-gray-50/60 hover:bg-white hover:border-[#55C5D0]/50 hover:shadow-sm transition-all p-3 cursor-pointer ${
                                    withMatches ? 'flex flex-col sm:flex-row gap-3' : ''
                                } ${
                                    opening === rowKey(person) ? 'opacity-60 pointer-events-none' : ''
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                    <span className="font-semibold text-gray-900">{person.name}</span>
                                    {person.updated_at && (
                                        <span className="text-xs text-gray-400">
                                            {dateLabel(person)} {formatDate(person.updated_at)}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                    {person.email && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                            {person.email}
                                        </span>
                                    )}
                                    {person.phone && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                            {person.phone}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center rounded-full bg-[#55C5D0]/15 text-[#0e7490] text-xs font-medium px-2 py-0.5">
                                        {person.status_label}
                                    </span>
                                    <IntentChip person={person} />
                                    {person.reason_label && (
                                        <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-xs px-2 py-0.5">
                                            {person.reason_label}
                                        </span>
                                    )}
                                    {person.paused_until && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                            <CalendarClock className="w-3.5 h-3.5 text-gray-400" />
                                            Paused until {formatDate(person.paused_until)}
                                        </span>
                                    )}
                                    {person.purchase_suburb && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                            Bought in {person.purchase_suburb}
                                        </span>
                                    )}
                                    {person.purchase_date && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                            <CalendarCheck className="w-3.5 h-3.5 text-gray-400" />
                                            Purchased {formatDay(person.purchase_date)}
                                        </span>
                                    )}
                                    {PURCHASED.includes(person.status)
                                        && !person.purchase_suburb
                                        && !person.purchase_date && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-400 italic">
                                            No purchase details recorded
                                        </span>
                                    )}
                                    {person.wants_property_management && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5">
                                            <Home className="w-3.5 h-3.5" />
                                            Wants property management
                                        </span>
                                    )}
                                </div>
                                {withMatches && (
                                    <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600">
                                        <SlidersHorizontal className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                                        {preferenceParts(person.preferences).join(' · ') || (
                                            <span className="italic text-gray-400">No buying preferences saved</span>
                                        )}
                                    </div>
                                )}
                                </div>
                                {withMatches && (
                                    // Clicks here belong to the buttons (and the
                                    // notes dialog they open), never the row.
                                    <div
                                        className="sm:w-44 shrink-0 flex flex-col gap-1.5 sm:justify-center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setEditing({ ...person, identity: identityOf(person) })}
                                            className={`${ACTION_BTN} bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-50`}
                                        >
                                            <SlidersHorizontal className="w-3.5 h-3.5" />
                                            Buying Preferences
                                        </button>
                                        <ContactNotesButton
                                            identity={identityOf(person)}
                                            className={`${ACTION_BTN} bg-white border-gray-200 text-gray-700 hover:bg-gray-50`}
                                        />
                                        {person.preferences && (
                                            <button
                                                type="button"
                                                onClick={() => setMatching(person)}
                                                className={`${ACTION_BTN} border-[#55C5D0] bg-[#55C5D0] hover:bg-[#45b5c0] text-white`}
                                            >
                                                <Building2 className="w-3.5 h-3.5" />
                                                Match properties
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}

                    {truncated && !loading && !failed && (
                        <p className="text-xs text-gray-400 pt-1">
                            Showing the 500 most recent answers.
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                    <span className="text-sm text-gray-500">
                        {loading || failed
                            ? ''
                            : `${filtered.length} ${filtered.length === 1 ? 'buyer' : 'buyers'}. Click anyone to open their record.`}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                    >
                        Close
                    </button>
                </div>
            </div>
            {matching && (
                <BuyerMatchPropertiesModal person={matching} onClose={() => setMatching(null)} />
            )}
            {editing && (
                <BuyerPreferencesModal
                    identity={editing.identity}
                    title={editing.name}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        quietReload.current = true;
                        setReloadKey((k) => k + 1);
                    }}
                />
            )}
        </div>
    );
};

export default BuyerSearchStatusPeopleModal;
